/**
 * ServiceSelection Component
 *
 * Displays a list of available services for the user to select from.
 * Used as the first step in the service booking flow.
 */
import RichText from '../../../../components/ui/RichText';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PublicProduct,
  isPointsPriced,
  resolveProductPointCost,
  resolveProductPrice,
} from '../../../../types/product';
import LoadingSpinner from '../../../../components/core/LoadingSpinner';
import { planChargeForViewer } from '../../../../utils/planPricing';
import ProductThumbnail from '../../../../components/products/ProductThumbnail';

interface ServiceSelectionProps {
  /** List of available service options */
  services: PublicProduct[];
  /** Currently selected service ID */
  selectedServiceId: number | null;
  /** Callback when a service is selected */
  onServiceSelect: (serviceId: number) => void;
  /** Whether services are loading */
  loading?: boolean;
  /** Error message if services failed to load */
  error?: string | null;
  /** Currency code for price display */
  currency?: string;
  /**
   * True when this meeting type is paid for with POINTS. Every service then
   * shows a point cost instead of money — the same switch the host confirms in
   * the Advanced tab.
   */
  pointsMode?: boolean;
  /** The meeting type's default point cost, used when a service has no own. */
  defaultPointCost?: number | null;
  /**
   * Hide the price entirely. Used when the viewer's subscription already covers
   * this meeting type: showing a number they are not being asked for reads as a
   * second charge, when the whole point of their plan is that there isn't one.
   */
  hidePrice?: boolean;
  /**
   * What is left on the plan the viewer already holds.
   *
   * When present, a plan option stops advertising itself — no monthly price and
   * no "N per month", because they already bought both. What they need to know
   * is how many bookings they have left. `total: null` means unlimited.
   */
  planUsage?: { used: number; total: number | null } | null;
  /**
   * The plan the viewer already holds, known from their session alone.
   *
   * Independent of `planUsage`, which comes from a round trip. An option that
   * IS their plan must never quote them a monthly price — not while that
   * request is in flight, and not if it never answers.
   */
  currentPlanProductId?: number | null;
  /**
   * The monthly price of the plan the viewer holds, so a dearer plan can be
   * quoted as the DIFFERENCE. Null when they hold none.
   */
  currentPlanPrice?: number | null;
}

/**
 * Format price for display
 */
const formatPrice = (price: number | null | undefined, currency: string): string => {
  if (price === null || price === undefined || price === 0) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

/**
 * Format duration for display
 */
const formatDuration = (minutes: number, t: any): string => {
  if (minutes < 60) {
    return t('booking:serviceBooking.minutesShort', '{{count}} min', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return t('booking:serviceBooking.hoursShort', '{{count}} hr', { count: hours });
  }
  return t('booking:serviceBooking.hoursMinutesShort', '{{hours}} hr {{minutes}} min', {
    hours,
    minutes: remainingMinutes,
  });
};

const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  services,
  selectedServiceId,
  onServiceSelect,
  loading = false,
  error = null,
  currency = 'USD',
  pointsMode = false,
  defaultPointCost = null,
  hidePrice = false,
  planUsage = null,
  currentPlanProductId = null,
  currentPlanPrice = null,
}) => {
  const { t } = useTranslation();

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" color="primary" className="mb-4" />
        <p className="text-secondary-600">
          {t('booking:serviceBooking.loadingServices', 'Loading services...')}
        </p>
      </div>
    );
  }

  /**
   * ⚠️ THE SIGNAL IS RENDERED, NOT THE STRING.
   *
   * `error` carries whatever the fetch failed with — often
   * `error.response.data.message`, written in English for a log, sometimes an
   * axios line like "Request failed with status code 500". Printing it put
   * that in front of a guest on a Japanese booking page. Nothing it can hold
   * is more useful here than "it did not load", which we can at least say in
   * their language.
   */
  if (error) {
    return (
      <div className="alert-danger">
        <p>{t('common:errors.loadingFailed', 'Failed to load data. Please try again.')}</p>
      </div>
    );
  }

  // Empty state
  if (!services || services.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="h-16 w-16 mx-auto mb-4 text-secondary-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <p className="text-secondary-600">
          {t('booking:serviceBooking.noServicesAvailable', 'No services available at this time.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        {t('booking:serviceBooking.selectService', 'Select a Service')}
      </h3>

      <div className="grid gap-3">
        {services.map((service) => {
          const isSelected = selectedServiceId === service.id;
          // effectivePrice is the attachment's override when this meeting
          // type prices the product differently from the catalog.
          const displayPrice = resolveProductPrice(service);
          const hasPrice = displayPrice > 0;
          /**
           * Priced in points, and how many — both from the shared helpers, so
           * this screen, the meeting-type editor and the server all walk the
           * same chain. This used to read `price` directly for a points-priced
           * product, which skipped the attachment's own `pointCost` override:
           * the page advertised one number and the booking deducted another.
           */
          const optionInPoints = isPointsPriced(service, pointsMode);

          /**
           * This option IS the plan they are on.
           *
           * Suppresses the price on its own, without waiting for the
           * entitlement read — quoting someone the monthly price of the plan
           * they already pay for is the thing to avoid, and their session
           * already knows the answer.
           */
          const isMyPlan =
            service.kind === 'subscription' &&
            currentPlanProductId != null &&
            service.id === currentPlanProductId;

          /** What THIS viewer pays for this plan — the difference when upgrading. */
          const planCharge =
            service.kind === 'subscription'
              ? planChargeForViewer(displayPrice, currentPlanPrice)
              : null;
          const optionPointPrice = resolveProductPointCost(service, defaultPointCost);

          return (
            <button
              key={service.id}
              onClick={() => onServiceSelect(service.id)}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-all duration-200
                ${isSelected
                  ? 'border-brand bg-brand/5 shadow-sm'
                  : 'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
                }
              `}
              style={isSelected ? { borderColor: 'var(--brand-color, #2E7AA0)' } : undefined}
            >
              <div className="flex items-center gap-4">
                {/* The four-case ladder lives in ProductThumbnail — the
                    dialog that sells a pack and the shop shelf read the same
                    one, so one product cannot look like two. */}
                <ProductThumbnail product={service} size="md" />

                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-medium text-secondary-900 truncate">
                    {service.title}
                  </h4>

                  {/* Description */}
                  {service.description && (
                    <RichText
                      html={service.description}
                      className="mt-1 text-sm text-secondary-500"
                    />
                  )}

                  {/* What this option costs, and what it includes.

                      The unit comes from the PRODUCT, not the page: a single
                      product priced in points sits happily beside one priced in
                      money, and the page-level `pointsMode` only ever knew
                      about the meeting type's own point cost.

                      A subscription also says what its price BUYS — a monthly
                      figure means nothing without the number of bookings it
                      covers, and someone signed out is deciding whether to
                      subscribe at all. */}
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <span className="text-sm text-secondary-500 flex items-center gap-1">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {formatDuration(service.durationMinutes, t)}
                    </span>
                    {/* What a plan option says depends entirely on whether the
                        viewer is already on it.

                        ON IT: how many bookings are left. They are not choosing
                        whether to subscribe, and repeating the monthly price and
                        "N per month" describes a purchase they already made.

                        NOT ON IT: what the plan includes, because a monthly
                        figure means nothing without the number it buys. */}
                    {service.kind === 'subscription' && (
                      <span className="text-sm text-secondary-500">
                        {isMyPlan && !planUsage
                          ? /* Theirs, but the allowance has not come back yet.
                               Say so rather than quote "N per month", which
                               describes a purchase already made. */
                            t('booking:points.yourPlan', 'Your plan')
                          : planUsage
                          ? planUsage.total == null
                            ? t('booking:points.planUnlimited', 'Unlimited meetings')
                            : t(
                                'booking:points.planRemaining',
                                '{{count}} bookings left',
                                { count: Math.max(0, planUsage.total - planUsage.used) }
                              )
                          : service.allowanceCount == null
                            ? t('booking:points.planUnlimited', 'Unlimited meetings')
                            : t(
                                'booking:points.planAllowance',
                                '{{count}} meetings per {{period}}',
                                {
                                  count: service.allowanceCount,
                                  period: t(
                                    `booking:period.${service.allowancePeriod ?? 'month'}`,
                                    'month'
                                  ),
                                }
                              )}
                      </span>
                    )}

                    {/* `isMyPlan` is checked alongside `hidePrice` on purpose:
                        one comes from the session and is instant, the other
                        from a round trip. Either being true is enough to stop
                        quoting a price to someone already paying it. */}
                    {hidePrice || isMyPlan ? null : optionInPoints
                      ? optionPointPrice > 0 && (
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            {t('booking:points.balance', '{{count}} points', {
                              count: optionPointPrice,
                            })}
                          </span>
                        )
                      : hasPrice && (
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--brand-color, #2E7AA0)' }}
                          >
                            {/* On a dearer plan, someone already subscribed pays
                                the DIFFERENCE — their current plan is credited
                                against it. Quoting the sticker price overstates
                                what upgrading costs, usually by most of it. */}
                            {service.kind === 'subscription'
                              ? planCharge?.isUpgrade
                                ? t(
                                    'booking:points.upgradeFor',
                                    '+{{price}} / month to upgrade',
                                    {
                                      price: formatPrice(planCharge.amount, service.currency),
                                    }
                                  )
                                : t('booking:serviceBooking.perMonth', '{{price}} / month', {
                                    price: formatPrice(displayPrice, service.currency),
                                  })
                              : formatPrice(displayPrice, service.currency)}
                          </span>
                        )}
                  </div>

                </div>

                {/* Selection indicator - moved outside content div for proper vertical centering */}
                <div
                  className={`
                    w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center
                    ${isSelected
                      ? 'border-brand bg-brand'
                      : 'border-secondary-300'
                    }
                  `}
                  style={isSelected ? {
                    borderColor: 'var(--brand-color, #2E7AA0)',
                    backgroundColor: 'var(--brand-color, #2E7AA0)',
                  } : undefined}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceSelection;
